(function (root, factory) {
  var attribution = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = attribution;
    return;
  }

  var tracker = attribution.create(root || {});
  root.DecoChatGPTAdsAttribution = tracker;
  tracker.boot();
})(typeof window !== 'undefined' ? window : undefined, function () {
  'use strict';

  var OPPREF_STORAGE_KEY = 'dg_openai_ads_oppref_v1';
  var OPPREF_STORED_AT_KEY = 'dg_openai_ads_oppref_stored_at_v1';
  var CART_SYNC_STORAGE_KEY = 'dg_openai_ads_oppref_cart_v1';
  var CONSENT_SYNC_STORAGE_KEY = 'dg_openai_ads_measurement_consent_cart_v1';
  var CART_CLEAN_STORAGE_KEY = 'dg_openai_ads_cart_clean_v1';
  var CART_ATTRIBUTE_KEY = 'openai_ads_oppref';
  var CONSENT_ATTRIBUTE_KEY = 'openai_ads_measurement_consent';
  var MAX_OPPREF_LENGTH = 512;
  var MAX_OPPREF_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  function storageGet(storage, key) {
    try {
      return storage && storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(storage, key, value) {
    try {
      if (!storage) return false;
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function storageRemove(storage, key) {
    try {
      if (!storage) return false;
      storage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  function validOppref(value) {
    if (typeof value !== 'string' || value.length < 1 || value.length > MAX_OPPREF_LENGTH) {
      return false;
    }

    return /^(?:[A-Za-z0-9._~+\/=\-]|%[0-9A-Fa-f]{2})+$/.test(value);
  }

  function opprefFromHref(href) {
    if (typeof href !== 'string') return null;
    try {
      var value = new URL(href).searchParams.get('oppref');
      return validOppref(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  function consentAllowed(root) {
    var privacy = root.Shopify && root.Shopify.customerPrivacy;
    if (!privacy) return false;

    try {
      return privacy.analyticsProcessingAllowed() === true &&
        privacy.marketingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function cartUpdateJsonUrl(root) {
    var configured = root.routes && root.routes.cart_update_url;
    var path = typeof configured === 'string' &&
      /^\/(?!\/)[A-Za-z0-9/_-]+(?:\.js)?$/.test(configured)
      ? configured
      : '/cart/update';

    return /\.js$/.test(path) ? path : path + '.js';
  }

  function ensurePrivacyApi(root, callback, attemptsLeft) {
    var remaining = typeof attemptsLeft === 'number' ? attemptsLeft : 20;

    if (root.Shopify && root.Shopify.customerPrivacy) {
      callback();
      return;
    }

    if (root.Shopify && typeof root.Shopify.loadFeatures === 'function') {
      root.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (!error && root.Shopify.customerPrivacy) callback();
        }
      );
      return;
    }

    if (remaining > 0 && typeof root.setTimeout === 'function') {
      root.setTimeout(function () {
        ensurePrivacyApi(root, callback, remaining - 1);
      }, 200);
    }
  }

  function create(root) {
    var state = {
      booted: false,
      clearExpiredOppref: false,
      pendingOppref: null,
      pendingRequest: null,
      pendingClearRequest: null,
    };

    function nowMs() {
      var clock = root.Date && typeof root.Date.now === 'function' ? root.Date : Date;
      return clock.now();
    }

    function removeStoredOppref() {
      storageRemove(root.localStorage, OPPREF_STORAGE_KEY);
      storageRemove(root.localStorage, OPPREF_STORED_AT_KEY);
      storageRemove(root.sessionStorage, CART_SYNC_STORAGE_KEY);
    }

    function currentOppref() {
      var fromUrl = opprefFromHref(root.location && root.location.href);
      if (fromUrl) {
        storageSet(root.localStorage, OPPREF_STORAGE_KEY, fromUrl);
        storageSet(root.localStorage, OPPREF_STORED_AT_KEY, String(nowMs()));
        state.clearExpiredOppref = false;
        return fromUrl;
      }

      var stored = storageGet(root.localStorage, OPPREF_STORAGE_KEY);
      if (!validOppref(stored)) return null;

      var storedAt = Number(storageGet(root.localStorage, OPPREF_STORED_AT_KEY));
      var age = nowMs() - storedAt;
      if (!Number.isFinite(storedAt) || storedAt <= 0 || age < 0 || age > MAX_OPPREF_AGE_MS) {
        removeStoredOppref();
        state.clearExpiredOppref = true;
        return null;
      }

      return stored;
    }

    function persistToCart(oppref) {
      if (state.pendingClearRequest) {
        return state.pendingClearRequest.then(sync, sync);
      }
      var consentSynced = storageGet(root.sessionStorage, CONSENT_SYNC_STORAGE_KEY) === 'granted';
      var opprefSynced = !state.clearExpiredOppref &&
        (!oppref || storageGet(root.sessionStorage, CART_SYNC_STORAGE_KEY) === oppref);
      if (consentSynced && opprefSynced) {
        return Promise.resolve(false);
      }

      if (state.pendingRequest) {
        if (state.pendingOppref === oppref) return state.pendingRequest;
        return state.pendingRequest.then(sync, sync);
      }

      if (typeof root.fetch !== 'function') return Promise.resolve(false);

      var attributes = {};
      attributes[CONSENT_ATTRIBUTE_KEY] = 'granted';
      if (oppref) attributes[CART_ATTRIBUTE_KEY] = oppref;
      if (state.clearExpiredOppref) attributes[CART_ATTRIBUTE_KEY] = '';
      var body = JSON.stringify({ attributes: attributes });
      state.pendingOppref = oppref;

      var request = root.fetch(cartUpdateJsonUrl(root), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: body,
        credentials: 'same-origin',
        keepalive: true,
      })
        .then(function (response) {
          if (!response || !response.ok || typeof response.json !== 'function') {
            throw new Error('ChatGPT Ads cart attribution request failed');
          }
          return response.json();
        })
        .then(function (cart) {
          var attributes = cart && cart.attributes || {};
          if (attributes[CONSENT_ATTRIBUTE_KEY] !== 'granted') {
            throw new Error('ChatGPT Ads measurement consent was not confirmed');
          }
          if (oppref && attributes[CART_ATTRIBUTE_KEY] !== oppref) {
            throw new Error('ChatGPT Ads cart attribution was not confirmed');
          }
          if (state.clearExpiredOppref && attributes[CART_ATTRIBUTE_KEY]) {
            throw new Error('Expired ChatGPT Ads cart attribution was not cleared');
          }
          storageSet(root.sessionStorage, CONSENT_SYNC_STORAGE_KEY, 'granted');
          storageRemove(root.sessionStorage, CART_CLEAN_STORAGE_KEY);
          if (oppref) storageSet(root.sessionStorage, CART_SYNC_STORAGE_KEY, oppref);
          if (state.clearExpiredOppref) {
            storageRemove(root.sessionStorage, CART_SYNC_STORAGE_KEY);
            state.clearExpiredOppref = false;
          }
          return true;
        });

      state.pendingRequest = request.then(
        function (result) {
          state.pendingOppref = null;
          state.pendingRequest = null;
          return result;
        },
        function () {
          state.pendingOppref = null;
          state.pendingRequest = null;
          return false;
        }
      );

      return state.pendingRequest;
    }

    function sync() {
      if (!consentAllowed(root)) return Promise.resolve(false);

      var oppref = currentOppref();
      return oppref ? persistToCart(oppref) : clearAttribution(true);
    }

    function clearAttribution(force) {
      if (state.pendingRequest) {
        return state.pendingRequest.then(function () {
          return clearAttribution(force);
        }, function () {
          return clearAttribution(force);
        });
      }
      if (state.pendingClearRequest) return state.pendingClearRequest;

      var storedOppref = storageGet(root.localStorage, OPPREF_STORAGE_KEY);
      var syncedOppref = storageGet(root.sessionStorage, CART_SYNC_STORAGE_KEY);
      var syncedConsent = storageGet(root.sessionStorage, CONSENT_SYNC_STORAGE_KEY);
      var cartClean = storageGet(root.sessionStorage, CART_CLEAN_STORAGE_KEY) === 'true';
      if (!storedOppref && !syncedOppref && !syncedConsent && (!force || cartClean)) {
        return Promise.resolve(false);
      }
      if (typeof root.fetch !== 'function') return Promise.resolve(false);

      var request = root.fetch(cartUpdateJsonUrl(root), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            openai_ads_oppref: '',
            openai_ads_measurement_consent: '',
          },
        }),
        credentials: 'same-origin',
        keepalive: true,
      })
        .then(function (response) {
          if (!response || !response.ok || typeof response.json !== 'function') {
            throw new Error('ChatGPT Ads cart attribution clear failed');
          }
          return response.json();
        })
        .then(function (cart) {
          var attributes = cart && cart.attributes || {};
          if (attributes[CART_ATTRIBUTE_KEY] || attributes[CONSENT_ATTRIBUTE_KEY]) {
            throw new Error('ChatGPT Ads cart attribution clear was not confirmed');
          }
          storageRemove(root.localStorage, OPPREF_STORAGE_KEY);
          storageRemove(root.localStorage, OPPREF_STORED_AT_KEY);
          storageRemove(root.sessionStorage, CART_SYNC_STORAGE_KEY);
          storageRemove(root.sessionStorage, CONSENT_SYNC_STORAGE_KEY);
          storageSet(root.sessionStorage, CART_CLEAN_STORAGE_KEY, 'true');
          return true;
        });

      state.pendingClearRequest = request.then(
        function (result) {
          state.pendingClearRequest = null;
          return result;
        },
        function () {
          state.pendingClearRequest = null;
          return false;
        }
      );
      return state.pendingClearRequest;
    }

    function handleConsent() {
      return consentAllowed(root) ? sync() : clearAttribution(true);
    }

    function boot() {
      if (state.booted) return;
      state.booted = true;

      if (root.document && typeof root.document.addEventListener === 'function') {
        root.document.addEventListener('visitorConsentCollected', handleConsent);
      }
      ensurePrivacyApi(root, handleConsent);
    }

    return {
      boot: boot,
      sync: sync,
    };
  }

  return {
    create: create,
    opprefFromHref: opprefFromHref,
    validOppref: validOppref,
    cartUpdateJsonUrl: cartUpdateJsonUrl,
  };
});
